if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.submitButtonPrice = this.submitButton.querySelector('[data-atc-price]');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        const requiresVariantSelection = this.dataset.requiresVariantSelection === 'true';
        const urlHasVariantParam = new URLSearchParams(window.location.search).has('variant');

        if (!requiresVariantSelection || urlHasVariantParam) {
          this.variantIdInput.disabled = false;
        }

        if (!window.productFormInitialisedSections) {
          window.productFormInitialisedSections = {};
        }
        const sectionId = this.dataset.sectionId;
        if (sectionId && !window.productFormInitialisedSections[sectionId]) {
          if (requiresVariantSelection && !urlHasVariantParam && window.variantStrings?.selectVariant) {
            this.toggleSubmitButton(true, window.variantStrings.selectVariant);
          }
          window.productFormInitialisedSections[sectionId] = true;
        }

        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      }

      connectedCallback() {
        if (!this.submitButtonPrice || this._atcPriceConnected) return;
        this._atcPriceConnected = true;

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event?.data?.sectionId !== this.dataset.sectionId) return;

          const priceText = this.getPriceTextFromHtml(event.data.html, event.data.sectionId);
          if (priceText !== null) {
            this.lastKnownPriceText = priceText;
            if (this.isSubmitButtonEnabled()) {
              this.submitButtonPrice.textContent = priceText;
            }
          }
        });

        if (this.submitButtonPrice.textContent.trim() === '') {
          const priceText = this.getPriceTextFromDocument(this.dataset.sectionId);
          if (priceText !== null && this.isSubmitButtonEnabled()) {
            this.submitButtonPrice.textContent = priceText;
            this.lastKnownPriceText = priceText;
          }
        } else {
          this.lastKnownPriceText = this.submitButtonPrice.textContent.trim();
        }
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
          this.variantChangeUnsubscriber = undefined;
        }
        this._atcPriceConnected = false;
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButton.setAttribute('aria-disabled', 'true');
          if (text) this.submitButtonText.textContent = text;
          if (this.submitButtonPrice) this.submitButtonPrice.textContent = '';
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
          if (this.submitButtonPrice) {
            const priceText = this.lastKnownPriceText || this.getPriceTextFromDocument(this.dataset.sectionId);
            this.submitButtonPrice.textContent = priceText || '';
          }
        }
      }

      isSubmitButtonEnabled() {
        return !this.submitButton.hasAttribute('disabled') && this.submitButton.getAttribute('aria-disabled') !== 'true';
      }

      getPriceTextFromHtml(html, sectionId) {
        if (!html || !sectionId) return null;
        const priceRoot = html.getElementById(`price-${sectionId}`);
        return this.getPriceTextFromRoot(priceRoot);
      }

      getPriceTextFromDocument(sectionId) {
        if (!sectionId) return null;
        const priceRoot = document.getElementById(`price-${sectionId}`);
        return this.getPriceTextFromRoot(priceRoot);
      }

      getPriceTextFromRoot(priceRoot) {
        if (!priceRoot) return null;
        const candidates = [
          '.price__sale .price-item--last',
          '.price__sale .price-item--sale',
          '.price__regular .price-item--regular',
          '.price-item--last',
          '.price-item--regular',
        ];
        for (const selector of candidates) {
          const el = priceRoot.querySelector(selector);
          const text = el?.textContent?.trim();
          if (text) return text;
        }
        return null;
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
